import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { ExtensionsRegistry } from 'vscode/vscode/vs/workbench/services/extensions/common/extensionsRegistry';
import { NotebookEditorPriority } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { SyncDescriptor } from 'vscode/vscode/vs/platform/instantiation/common/descriptors';
import { Extensions } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionFeatures';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';

const _moduleId = "vs/workbench/contrib/notebook/browser/notebookExtensionPoint";
const NotebookEditorContribution = ( (Object.freeze({
    type: 'type',
    displayName: 'displayName',
    selector: 'selector',
    priority: 'priority',
})));
const NotebookRendererContribution = ( (Object.freeze({
    id: 'id',
    displayName: 'displayName',
    mimeTypes: 'mimeTypes',
    entrypoint: 'entrypoint',
    hardDependencies: 'dependencies',
    optionalDependencies: 'optionalDependencies',
    requiresMessaging: 'requiresMessaging',
})));
const NotebookPreloadContribution = ( (Object.freeze({
    type: 'type',
    entrypoint: 'entrypoint',
    localResourceRoots: 'localResourceRoots',
})));
const notebookProviderContribution = {
    description: ( localizeWithPath(_moduleId, 0, 'Contributes notebook document provider.')),
    type: 'array',
    defaultSnippets: [{ body: [{ type: '', displayName: '', 'selector': [{ 'filenamePattern': '' }] }] }],
    items: {
        type: 'object',
        required: [
            NotebookEditorContribution.type,
            NotebookEditorContribution.displayName,
            NotebookEditorContribution.selector,
        ],
        properties: {
            [NotebookEditorContribution.type]: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 1, 'Type of the notebook.')),
            },
            [NotebookEditorContribution.displayName]: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 2, 'Human readable name of the notebook.')),
            },
            [NotebookEditorContribution.selector]: {
                type: 'array',
                description: ( localizeWithPath(_moduleId, 3, 'Set of globs that the notebook is for.')),
                items: {
                    type: 'object',
                    properties: {
                        filenamePattern: {
                            type: 'string',
                            description: ( localizeWithPath(_moduleId, 4, 'Glob that the notebook is enabled for.')),
                        },
                        excludeFileNamePattern: {
                            type: 'string',
                            description: ( localizeWithPath(_moduleId, 5, 'Glob that the notebook is disabled for.'))
                        }
                    }
                }
            },
            [NotebookEditorContribution.priority]: {
                type: 'string',
                markdownDeprecationMessage: ( localizeWithPath(
                    _moduleId,
                    6,
                    'Controls if the custom editor is enabled automatically when the user opens a file. This may be overridden by users using the `workbench.editorAssociations` setting.'
                )),
                enum: [
                    NotebookEditorPriority.default,
                    NotebookEditorPriority.option,
                ],
                markdownEnumDescriptions: [
                    ( localizeWithPath(
                        _moduleId,
                        7,
                        'The editor is automatically used when the user opens a resource, provided that no other default custom editors are registered for that resource.'
                    )),
                    ( localizeWithPath(
                        _moduleId,
                        8,
                        'The editor is not automatically used when the user opens a resource, but a user can switch to the editor using the `Reopen With` command.'
                    )),
                ],
                default: 'default'
            }
        }
    }
};
const defaultRendererSnippet = ( (Object.freeze({ id: '', displayName: '', mimeTypes: [''], entrypoint: '' })));
const notebookRendererContribution = {
    description: ( localizeWithPath(_moduleId, 9, 'Contributes notebook output renderer provider.')),
    type: 'array',
    defaultSnippets: [{ body: [defaultRendererSnippet] }],
    items: {
        defaultSnippets: [{ body: defaultRendererSnippet }],
        allOf: [
            {
                type: 'object',
                required: [
                    NotebookRendererContribution.id,
                    NotebookRendererContribution.displayName,
                ],
                properties: {
                    [NotebookRendererContribution.id]: {
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 10, 'Unique identifier of the notebook output renderer.')),
                    },
                    [NotebookRendererContribution.displayName]: {
                        type: 'string',
                        description: ( localizeWithPath(_moduleId, 11, 'Human readable name of the notebook output renderer.')),
                    },
                    [NotebookRendererContribution.hardDependencies]: {
                        type: 'array',
                        uniqueItems: true,
                        items: { type: 'string' },
                        markdownDescription: ( localizeWithPath(
                            _moduleId,
                            12,
                            'List of kernel dependencies the renderer requires. If any of the dependencies are present in the `NotebookKernel.preloads`, the renderer can be used.'
                        )),
                    },
                    [NotebookRendererContribution.optionalDependencies]: {
                        type: 'array',
                        uniqueItems: true,
                        items: { type: 'string' },
                        markdownDescription: ( localizeWithPath(
                            _moduleId,
                            13,
                            'List of soft kernel dependencies the renderer can make use of. If any of the dependencies are present in the `NotebookKernel.preloads`, the renderer will be preferred over renderers that don\'t interact with the kernel.'
                        )),
                    },
                    [NotebookRendererContribution.requiresMessaging]: {
                        default: 'never',
                        enum: [
                            'always',
                            'optional',
                            'never',
                        ],
                        enumDescriptions: [
                            ( localizeWithPath(
                                _moduleId,
                                14,
                                'Messaging is required. The renderer will only be used when it\'s part of an extension that can be run in an extension host.'
                            )),
                            ( localizeWithPath(
                                _moduleId,
                                15,
                                'The renderer is better with messaging available, but it\'s not requried.'
                            )),
                            ( localizeWithPath(_moduleId, 16, 'The renderer does not require messaging.')),
                        ],
                        description: ( localizeWithPath(
                            _moduleId,
                            17,
                            'Defines how and if the renderer needs to communicate with an extension host, via `createRendererMessaging`. Renderers with stronger messaging requirements may not work in all environments.'
                        )),
                    },
                }
            },
            {
                oneOf: [
                    {
                        required: [
                            NotebookRendererContribution.entrypoint,
                            NotebookRendererContribution.mimeTypes,
                        ],
                        properties: {
                            [NotebookRendererContribution.mimeTypes]: {
                                type: 'array',
                                description: ( localizeWithPath(_moduleId, 18, 'Set of globs that the notebook is for.')),
                                items: {
                                    type: 'string'
                                }
                            },
                            [NotebookRendererContribution.entrypoint]: {
                                description: ( localizeWithPath(_moduleId, 19, 'File to load in the webview to render the extension.')),
                                type: 'string',
                            },
                        }
                    },
                    {
                        required: [
                            NotebookRendererContribution.entrypoint,
                        ],
                        properties: {
                            [NotebookRendererContribution.entrypoint]: {
                                description: ( localizeWithPath(_moduleId, 19, 'File to load in the webview to render the extension.')),
                                type: 'object',
                                required: ['extends', 'path'],
                                properties: {
                                    extends: {
                                        type: 'string',
                                        description: ( localizeWithPath(_moduleId, 20, 'Existing renderer that this one extends.')),
                                    },
                                    path: {
                                        type: 'string',
                                        description: ( localizeWithPath(_moduleId, 19, 'File to load in the webview to render the extension.')),
                                    },
                                }
                            },
                        }
                    }
                ]
            }
        ]
    }
};
const notebookPreloadContribution = {
    description: ( localizeWithPath(_moduleId, 21, 'Contributes notebook preloads.')),
    type: 'array',
    defaultSnippets: [{ body: [{ type: '', entrypoint: '' }] }],
    items: {
        type: 'object',
        required: [
            NotebookPreloadContribution.type,
            NotebookPreloadContribution.entrypoint
        ],
        properties: {
            [NotebookPreloadContribution.type]: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 22, 'Type of the notebook.')),
            },
            [NotebookPreloadContribution.entrypoint]: {
                type: 'string',
                description: ( localizeWithPath(_moduleId, 23, 'Path to file loaded in the webview.')),
            },
            [NotebookPreloadContribution.localResourceRoots]: {
                type: 'array',
                items: { type: 'string' },
                description: ( localizeWithPath(
                    _moduleId,
                    24,
                    'Paths to additional resources that should be allowed in the webview.'
                )),
            },
        }
    }
};
const notebooksExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'notebooks',
    jsonSchema: notebookProviderContribution,
    activationEventsGenerator: (contribs, result) => {
        for (const contrib of contribs) {
            if (contrib.type) {
                result.push(`onNotebookSerializer:${contrib.type}`);
            }
        }
    }
});
const notebookRendererExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'notebookRenderer',
    jsonSchema: notebookRendererContribution,
    activationEventsGenerator: (contribs, result) => {
        for (const contrib of contribs) {
            if (contrib.id) {
                result.push(`onRenderer:${contrib.id}`);
            }
        }
    }
});
const notebookPreloadExtensionPoint = ExtensionsRegistry.registerExtensionPoint({
    extensionPoint: 'notebookPreload',
    jsonSchema: notebookPreloadContribution,
});
class NotebooksDataRenderer extends Disposable {
    constructor() {
        super(...arguments);
        this.type = 'table';
    }
    shouldRender(manifest) {
        return !!manifest.contributes?.notebooks;
    }
    render(manifest) {
        const contrib = manifest.contributes?.notebooks || [];
        if (!contrib.length) {
            return { data: { headers: [], rows: [] }, dispose: () => { } };
        }
        const headers = [
            ( localizeWithPath(_moduleId, 25, "ID")),
            ( localizeWithPath(_moduleId, 26, "Name")),
        ];
        const rows = ( (contrib
            .sort((a, b) => a.type.localeCompare(b.type))
            .map(notebook => {
            return [
                notebook.type,
                notebook.displayName
            ];
        })));
        return {
            data: {
                headers,
                rows
            },
            dispose: () => { }
        };
    }
}
class NotebookRenderersDataRenderer extends Disposable {
    constructor() {
        super(...arguments);
        this.type = 'table';
    }
    shouldRender(manifest) {
        return !!manifest.contributes?.notebookRenderer;
    }
    render(manifest) {
        const contrib = manifest.contributes?.notebookRenderer || [];
        if (!contrib.length) {
            return { data: { headers: [], rows: [] }, dispose: () => { } };
        }
        const headers = [
            ( localizeWithPath(_moduleId, 27, "Name")),
            ( localizeWithPath(_moduleId, 28, "Mimetypes")),
        ];
        const rows = ( (contrib
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .map(notebookRenderer => {
            return [
                notebookRenderer.displayName,
                notebookRenderer.mimeTypes.join(',')
            ];
        })));
        return {
            data: {
                headers,
                rows
            },
            dispose: () => { }
        };
    }
}
( (Registry.as(Extensions.ExtensionFeaturesRegistry))).registerExtensionFeature({
    id: 'notebooks',
    label: ( localizeWithPath(_moduleId, 29, "Notebooks")),
    access: {
        canToggle: false
    },
    renderer: ( (new SyncDescriptor(NotebooksDataRenderer))),
});
( (Registry.as(Extensions.ExtensionFeaturesRegistry))).registerExtensionFeature({
    id: 'notebookRenderer',
    label: ( localizeWithPath(_moduleId, 30, "Notebook Renderers")),
    access: {
        canToggle: false
    },
    renderer: ( (new SyncDescriptor(NotebookRenderersDataRenderer))),
});

export { notebookPreloadExtensionPoint, notebookRendererExtensionPoint, notebooksExtensionPoint };
